import type { BoardroomNotice, BoardroomObservation } from '../../../types';

/** How many of the observer's findings the Brief lists before "N more". */
export const FINDINGS_SHOWN = 4;

/** The observer's findings, not its upkeep notes (data problems, duplicates). */
export function briefFindings(observations: BoardroomObservation[]): BoardroomObservation[] {
  return observations.filter(o => o.sort === 'finding');
}

/** The sites the room keeps finding and has not put on the board. */
export function briefSites(notices: BoardroomNotice[]): BoardroomNotice[] {
  return notices.filter(n => n.key.startsWith('ranks:'));
}

/**
 * WHAT THE ROOM NOTICED, in the Brief rather than behind a button.
 *
 * The observer's findings ("solo practices still use the reporting built into
 * their EHR") and the sites that keep turning up in the room's searches without
 * being on the board. They sat in two trays, Noticed and Observed, among tag
 * patterns and upkeep notes. Complaints nobody addresses are the Brief's own
 * complaints section, so they are not repeated here.
 */
export default function BriefNoticed({
  observations,
  notices,
  busy = false,
  onSeen,
  onDismiss,
  onShowRanks,
  onOpenItem,
  onMore,
  setAside = 0,
  onSetAside,
}: {
  observations: BoardroomObservation[];
  notices: BoardroomNotice[];
  busy?: boolean;
  /** Mark a finding read: it leaves the Brief. */
  onSeen: (o: BoardroomObservation) => void;
  /** Set a site aside: not a player, do not offer it again. */
  onDismiss: (n: BoardroomNotice) => void;
  onShowRanks: () => void;
  onOpenItem: (id: number) => void;
  /** Every finding and upkeep note, in Observed. */
  onMore?: () => void;
  /** Sites set aside earlier, and where to bring one back. */
  setAside?: number;
  onSetAside?: () => void;
}) {
  const findings = briefFindings(observations);
  const sites = briefSites(notices);
  if (!findings.length && !sites.length && !setAside) return null;
  const more = findings.length - FINDINGS_SHOWN;
  return (
    <section
      className="flex flex-col gap-2 rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800"
      data-testid="brief-noticed"
    >
      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        What the room noticed
      </h3>
      {findings.length > 0 && (
        <ul className="flex flex-col gap-2">
          {findings.slice(0, FINDINGS_SHOWN).map(o => (
            <li key={o.id} className="flex items-start gap-2 text-sm" data-testid={`brief-finding-${o.id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-gray-800 dark:text-gray-100">{o.title}</p>
                {o.cites.length > 0 && (
                  <p className="mt-0.5 flex flex-wrap gap-x-2 text-xs text-gray-500 dark:text-gray-400">
                    <span>from</span>
                    {o.cites.slice(0, 3).map(c => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => onOpenItem(c.id)}
                        className="text-indigo-600 hover:underline dark:text-indigo-300"
                      >
                        {c.title}
                      </button>
                    ))}
                    {o.cites.length > 3 && <span>+{o.cites.length - 3}</span>}
                  </p>
                )}
              </div>
              <button
                type="button"
                disabled={busy}
                onClick={() => onSeen(o)}
                title="Mark it read; it leaves the Brief"
                className="shrink-0 text-xs text-gray-400 hover:text-gray-700 disabled:opacity-50 dark:hover:text-gray-200"
              >
                Seen
              </button>
            </li>
          ))}
        </ul>
      )}
      {sites.length > 0 && (
        <ul className="flex flex-col gap-1.5 border-t border-gray-100 pt-2 dark:border-gray-700">
          {sites.map(n => (
            <li
              key={n.key}
              className="flex flex-wrap items-baseline gap-x-2 text-sm"
              data-testid={`brief-site-${n.key}`}
            >
              <span className="text-gray-800 dark:text-gray-100">{n.title}</span>
              <span className="text-xs text-gray-500 dark:text-gray-400">{n.why}</span>
              <span className="ml-auto flex gap-3 text-xs">
                <button
                  type="button"
                  onClick={onShowRanks}
                  className="text-indigo-600 hover:underline dark:text-indigo-300"
                >
                  Show who ranks
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onDismiss(n)}
                  title="Not a player: do not offer it again"
                  className="text-gray-400 hover:text-gray-700 disabled:opacity-50 dark:hover:text-gray-200"
                >
                  Dismiss
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
      {((more > 0 && onMore) || (setAside > 0 && onSetAside)) && (
        <div className="flex gap-4 text-xs">
          {more > 0 && onMore && (
            <button type="button" onClick={onMore} className="text-indigo-600 hover:underline dark:text-indigo-300">
              {more} more finding{more === 1 ? '' : 's'} →
            </button>
          )}
          {setAside > 0 && onSetAside && (
            <button
              type="button"
              onClick={onSetAside}
              className="text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-100"
              data-testid="brief-set-aside"
            >
              Set aside ({setAside})
            </button>
          )}
        </div>
      )}
    </section>
  );
}
